import { CAPABILITY_MANAGEMENT, PERMISSION_DATA } from "api/ApiPath";
import { ActionInjection } from "app/actions/di/ActionInjection";
import { ActionUseCase } from "app/actions/domain/useCases/ActionUseCase";
import { BaseApiResponseModel } from "app/based/baseApiModel/baseApiResponseModel/BaseApiResponseModel";
import { ListBaseApiResponseModel } from "app/based/baseApiModel/baseApiResponseModel/ListBaseApiResponseModel";
import { BaseFilterListRequestModel } from "app/based/baseApiModel/baseFilterListModel/BaseFilterListRequestModel";
import { BaseFilterListResponseModel } from "app/based/baseApiModel/baseFilterListModel/BaseFilterListResponseModel";
import { FilterInjection } from "app/filters/di/FilterInjection";
import { IFilterRepository } from "app/filters/domain/repository/IFilterRepository";
import { CapabilityInjection } from "features/capabilityManagement/di/CapabilityInjection";
import { injectable } from "tsyringe";
import { DataFormat } from "utils/format/DataFormat";
import { strings } from "utils/localizedStrings";
import {
  ArrayEditActionEntity,
  CapabilityActionRequestEntity,
} from "../entities/CapabilityActionRequestEntity";
import { CapabilityDetailEntity } from "../entities/CapabilityDetailEntity";
import { CapabilityRequestEntity } from "../entities/CapabilityRequestEntity";
import { CapabilityResponseEntity } from "../entities/CapabilityResponseEntity";
import { ICapabilityRepository } from "../repositories/ICapabilityRepository";

@injectable()
export class CapabilityUseCase {
  private capabilityRepo: ICapabilityRepository;
  private filterRepo: IFilterRepository;
  private actionUseCase: ActionUseCase;

  constructor() {
    this.actionUseCase = ActionInjection.getUseCase();
    this.capabilityRepo = CapabilityInjection.getRepo();
    this.filterRepo = FilterInjection.getRepo();
  }

  private checkParams(params: CapabilityRequestEntity) {
    const finalParams = { ...params };
    delete finalParams.created;

    Object.keys(finalParams).forEach((rawKey) => {
      const key = rawKey as keyof CapabilityRequestEntity;
      if (
        finalParams[key] === strings.GlobalLabels.All ||
        finalParams[key] === "" ||
        typeof finalParams[key] === "undefined"
      ) {
        delete finalParams[key];
      }
    });

    return finalParams;
  }

  private buildArrayEditBody(
    bodyVal: Array<string>,
    comparedVal: Array<BaseFilterListResponseModel>,
  ): ArrayEditActionEntity | undefined {
    const selectedVal = (Array.isArray(bodyVal) ? bodyVal : []).filter(
      Boolean,
    ) as string[];
    const currentVal =
      comparedVal?.map((item) => item?.code).filter(Boolean) || [];

    const add = selectedVal.filter((item) => !currentVal.includes(item));
    const erase = currentVal.filter((item) => !selectedVal.includes(item));

    if (!add.length && !erase.length) {
      return undefined;
    }

    return {
      add: add.length ? add : undefined,
      erase: erase.length ? erase : undefined,
    };
  }

  private updateBody(
    body: CapabilityActionRequestEntity,
    detailInfo: CapabilityDetailEntity,
  ) {
    const keysToAvoid = ["code", "menus", "configuration"];
    const finalMenu = !body["code"]
      ? body?.menus
      : this.buildArrayEditBody(
          body?.menus as Array<string>,
          detailInfo?.menus,
        );
    return {
      keysToAvoid,
      afterBody: {
        ...body,
        menus: finalMenu,
        configuration:
          typeof body?.configuration === "string" && body?.configuration
            ? JSON.parse(DataFormat.autoCorrectJSON(String(body.configuration)))
            : body?.configuration,
      } as typeof body,
    };
  }

  async getList(
    params: CapabilityRequestEntity,
  ): Promise<ListBaseApiResponseModel<CapabilityResponseEntity>> {
    return await this.capabilityRepo.getList(this.checkParams(params));
  }

  async getCapabilityDetail(
    params: CapabilityRequestEntity,
  ): Promise<BaseApiResponseModel<CapabilityDetailEntity>> {
    return await this.capabilityRepo.getDetail(this.checkParams(params));
  }

  async createCapability(
    body: CapabilityActionRequestEntity,
    detailInfo: CapabilityDetailEntity,
  ): Promise<BaseApiResponseModel<CapabilityDetailEntity> | undefined> {
    const updatedBody = this.updateBody(body, detailInfo);
    return await this.actionUseCase.create({
      path: CAPABILITY_MANAGEMENT.CREATE,
      inputValue: updatedBody?.afterBody,
    });
  }

  async updateCapability(
    body: CapabilityActionRequestEntity,
    detailInfo: CapabilityDetailEntity,
  ): Promise<BaseApiResponseModel<CapabilityDetailEntity> | undefined> {
    const updatedBody = this.updateBody(body, detailInfo);
    return await this.actionUseCase.update({
      path: CAPABILITY_MANAGEMENT.UPDATE,
      inputValue: updatedBody?.afterBody,
      keysToAvoid: updatedBody?.keysToAvoid,
      comparedValue: detailInfo,
      allValToString: true,
    });
  }

  async deleteCapability(
    params: CapabilityRequestEntity,
  ): Promise<BaseApiResponseModel<CapabilityDetailEntity> | undefined> {
    return await this.actionUseCase.delete({
      path: CAPABILITY_MANAGEMENT.DELETE,
      inputValue: params,
    });
  }

  async getMenu(
    params: BaseFilterListRequestModel,
  ): Promise<ListBaseApiResponseModel<BaseFilterListResponseModel>> {
    const path = PERMISSION_DATA.FILTER_LIST;
    return await this.filterRepo.getFilterList(path, params);
  }
}
